/**
 * Codec for the metric-type strings carried on feed items.
 *
 * Two shapes exist today:
 *
 * 1. Standard feeds emit `<metric>_<N>pct_<P>w` (e.g. `price_index_5pct_12w`).
 *    `evaluateFeedRules` builds the string; UI code reverses it to drive
 *    routing and highlighting.
 *
 * 2. Correlated feeds emit the rule's raw `name` (e.g. `supply_shortage_signal`).
 *    Periods and thresholds are not encoded — they live on the rule. The
 *    constituent metrics that triggered the alert are content-specific and
 *    tracked here in `CORRELATED_FEED_METRICS`.
 *
 * Producers and consumers both go through this module so the format stays in
 * one place. The round-trip test in `feedMetricType.test.ts` fails if anyone
 * changes the encoder without also updating the decoder.
 */

export interface FeedMetricType {
    metric: string;
    threshold: number;
    periods: number;
}

const FEED_METRIC_TYPE_RE = /^(.+?)_(\d+)pct_(\d+)w$/;

export function encodeFeedMetricType({ metric, threshold, periods }: FeedMetricType): string {
    return `${metric}_${Math.round(threshold * 100)}pct_${periods}w`;
}

export function decodeFeedMetricType(s: string): FeedMetricType | null {
    const m = s.match(FEED_METRIC_TYPE_RE);
    if (!m) return null;
    return {
        metric: m[1],
        threshold: parseInt(m[2], 10) / 100,
        periods: parseInt(m[3], 10),
    };
}

export const CORRELATED_FEED_METRICS: Record<string, readonly string[]> = {
    supply_shortage_signal: ['price_index', 'stock_index', 'lead_time_index'],
};

export function getCorrelatedFeedMetrics(metricType: string): readonly string[] | null {
    return CORRELATED_FEED_METRICS[metricType] ?? null;
}
